import type { PlayerCardData } from './types'
import { headerColors, autoContrastText } from '@/features/_shared/teamColor'
import { InfoTip } from '@/components/ui/info-tip'
import { salaryUSD } from './format'
import { handToken } from '@/features/_shared/handedness'
import { archetypePositionLabel } from '@/features/_shared/archetypePosition'

export function PlayerCardHeader({ player }: { player: PlayerCardData }) {
  const c = headerColors(player.team?.colorPrimary, player.team?.colorSecondary)
  const chipTone = c.fgIsDark ? 'bg-black/10 ring-black/20' : 'bg-white/15 ring-white/25'
  // The position badge becomes a solid SECONDARY-color patch (same idea as the
  // playoffs score-cap) when the team set a distinct second color; otherwise it
  // stays the translucent chip. `border` is the secondary (or falls back to the
  // primary `bg` when none), so border !== bg means a real second color exists.
  const hasSecondary = c.border !== c.bg
  const subtitle = player.team ? `${player.team.city} ${player.team.name}` : 'Free Agent'
  // Pending position change — where this player moves at the next season flip
  // (dbo.pendingpositionchanges). Surfaced as "→ {pos}"; the old backward
  // start-of-year position is dropped (ratings history covers that), #588.
  const pendingPos = player.bio.pendingPositionChange?.trim()
  // Multi-Position card: the badge shows the CARD's primary label rather than
  // players.posn (a right fielder carded at corner outfield plays both corners,
  // so his stored "RF" understates him and would disagree with the chips in the
  // ratings section), and the rest of the template trails it. Same treatment as
  // the full player page's vitals line.
  const cardPositions = (player.positionRatings ?? [])
    .map((r) => archetypePositionLabel(r.abbr))
    .filter((a) => !!a)
  const showTemplate = cardPositions.length > 1

  const hasPendingChange =
    !!pendingPos && pendingPos.toUpperCase() !== player.position?.trim().toUpperCase()
  return (
    <div
      className="relative shrink-0 overflow-hidden rounded-t-lg px-4 pb-3.5 pt-3 landscape-short:pb-2 landscape-short:pt-2"
      style={{ backgroundColor: c.bg, color: c.fg, borderBottom: `4px solid ${c.border}` }}
    >
      {/* Secondary-color accent stripe sweeping the top — a trading-card band. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-1"
        style={{ backgroundColor: c.border }}
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* Position badge chip — matches the slate position chips on the FA/roster anchors,
              but rendered on the team-color field so it reads as part of the card crest.
              Outlined in the foreground color so it stays legible on any team bg
              (including the navy free-agent / retired fallback). */}
          <span
            className={`mb-1 inline-flex min-w-[30px] items-center justify-center rounded px-2 py-0.5 text-[12px] font-bold uppercase tracking-wide ring-1 ring-inset ${hasSecondary ? 'ring-black/10' : chipTone}`}
            style={hasSecondary ? { backgroundColor: c.border, color: autoContrastText(c.border) } : { color: c.fg }}
          >
            {showTemplate ? cardPositions[0] : player.position}
          </span>
          {/* The secondary positions echo the primary badge as outlined chips, so
              the set reads as one group. Outlined rather than filled: the filled
              patch is the crest, and it belongs to the position he actually is. */}
          {showTemplate && cardPositions.slice(1).map((p) => (
            <span
              key={p}
              className="ml-1 inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset opacity-90"
              style={{ color: c.fg, borderColor: c.fg }}
            >
              {p}
            </span>
          ))}
          {hasPendingChange && (
            <InfoTip
              content={`Changing to ${pendingPos} at the next season flip`}
              className="ml-1.5 align-middle text-[11px] font-semibold opacity-90 cursor-pointer"
            >
              <span style={{ color: c.fg }}>→ {pendingPos}</span>
            </InfoTip>
          )}
          <div className="text-[1.7rem] font-black leading-tight tracking-tight drop-shadow-sm landscape-short:text-xl">
            <span className="font-semibold opacity-75">{player.firstName}</span>{' '}
            {player.lastName}
          </div>
          {/* Team-color accent rule under the name. Dropped on landscape phones
              to reclaim vertical space for the scroll area. */}
          <div className="mt-1.5 h-1 w-12 rounded-full landscape-short:hidden" style={{ backgroundColor: c.border }} />
          {/* Team name treated like a jersey nameplate — uppercase, spaced. */}
          <div className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] opacity-90">
            {subtitle}
          </div>
          {/* Functional vitals — the decision-relevant facts (age, hands, status),
              kept in the prime spot rather than buried in a section. The
              sim-determined "retiring after this season" flag rides here too so
              it shows year-round, not only in the postseason next-salary line. */}
          <div className="mt-1 text-sm font-medium tabular-nums opacity-90">
            {/* Pitchers show the throwing hand alone (RHP/LHP) — they never bat.
                `isPitcher` is the server-computed flag the stats tabs key off. */}
            {`${player.bio.age ?? '—'}yo · ${handToken(player.bio.bats, player.bio.throws, player.isPitcher)} · ${player.bio.rosterLevel}`}
            {player.contract.retiring && !player.bio.retired && (
              <span className="ml-1 font-semibold uppercase tracking-wide"> · retiring</span>
            )}
          </div>
        </div>

        {/* Salary as the card's value stamp, pinned to the BOTTOM-right corner and
            baseline-aligned with the vitals line on the left. Deliberately
            typographic — no ring / border / backdrop: the old pill shared the
            "Full page"/Close chrome and, sitting right under them, read as a third
            control in that top-right cluster. Bottom-aligning it also clears those
            floating dialog controls up top without needing a spacer. */}
        <div className="flex shrink-0 flex-col items-end justify-end self-stretch text-right">
          {!player.bio.retired && player.contract.salary > 0 && (
            <span className="text-xl font-black leading-none tabular-nums drop-shadow-sm landscape-short:text-lg" style={{ color: c.fg }}>
              {salaryUSD(player.contract.salary)}
            </span>
          )}
          {/* Next-season salary only exists in the postseason — it lives with the
              current salary so it can come and go without disturbing the layout. */}
          {!player.bio.retired && player.contract.nextSeasonSalaryBase != null && (
            <span className="mt-0.5 text-[11px] font-semibold tabular-nums opacity-90" style={{ color: c.fg }}>
              Next: {player.contract.retiring ? 'Retiring' : salaryUSD(player.contract.nextSeasonSalary)}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
